import {
  EN_158042012_ID,
  ISO_14025_2026_ID,
  ISO_14025_ID,
  ISO_21930_ID,
  NORTH_AMERICA_PILOT_PCR,
} from 'components/v2/compiler/constants';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import type { DictionaryValue, Option } from 'types/types';
import { getOptionsFromDictionary } from 'util/utils';

export type ConformityStandardsPcr = {
  id?: string;
  isConstruction?: boolean;
  isISO219302017Available?: boolean;
  isEN158042012A2Available?: boolean;
};

/** ISO 14025:2006 and ISO 14025:2026 - the standards every PCR allows. */
const isIso14025Standard = (standardId: string): boolean => standardId === ISO_14025_ID || standardId === ISO_14025_2026_ID;

/** The saved standard keys, trimmed, without blank ones. A comma-separated value and an array are both accepted. */
const parseStandardKeys = (saved: string | string[] | undefined): string[] =>
  (Array.isArray(saved) ? saved : (saved ?? '').split(',')).map((key) => String(key).trim()).filter((key) => key.length > 0);

/**
 * Whether a Statement of conformity with standards option is disabled for the given PCR.
 * ISO 14025:2006 and ISO 14025:2026 are always enabled; a construction PCR enables every option;
 * otherwise EN 15804 and ISO 21930 follow the PCR flags (ISO 21930 is also enabled for the
 * North America pilot PCR). Unknown ids are disabled.
 */
export const isConformityStandardDisabled = (standardId: string, pcr?: ConformityStandardsPcr): boolean => {
  if (isIso14025Standard(standardId)) {
    return false;
  }

  if (pcr?.isConstruction) {
    return false;
  }

  if (standardId === EN_158042012_ID) {
    return !pcr?.isEN158042012A2Available;
  }

  if (standardId === ISO_21930_ID) {
    const isPilotNorthAmericaPcr = pcr?.id?.toLowerCase() === NORTH_AMERICA_PILOT_PCR;
    return !pcr?.isISO219302017Available && !isPilotNorthAmericaPcr;
  }

  return true;
};

/**
 * The Statement of conformity with standards options: the ConformityStandards entries of the dictionary
 * in ascending Ord, each with isDisabled set by isConformityStandardDisabled for the given PCR.
 */
export const getConformityStandardOptions = (
  dictionary: DictionaryValue[] | undefined,
  pcr?: ConformityStandardsPcr
): Option[] =>
  getOptionsFromDictionary(dictionary, EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS).map((option) => ({
    ...option,
    isDisabled: isConformityStandardDisabled(option.value, pcr),
  }));

/**
 * The saved standards as dictionary options in their saved order, for display (FR-015).
 * The dictionary Ord orders only the select options. Keys that are not in the dictionary are left out.
 */
export const getSelectedConformityStandards = (
  saved: string | string[] | undefined,
  dictionary: DictionaryValue[] | undefined
): Option[] =>
  getSelectedStandardOptions(saved, getOptionsFromDictionary(dictionary, EPD_DICTIONARY_CODES.CONFORMITY_STANDARDS));

/**
 * The options of the saved keys, in their saved order - the value of a Standards conformance select.
 * Keys without an option are left out.
 */
export const getSelectedStandardOptions = <T extends Option>(saved: string | string[] | undefined, options: T[]): T[] =>
  parseStandardKeys(saved)
    .map((key) => options.find((option) => option.value === key))
    .filter((option): option is T => option !== undefined);

/**
 * The Copy group wizard rule for a PCR change (FR-006, new EPD, version 1).
 * The ids are trimmed and blank ones dropped first, so [""] (from "".split(',')) counts as an empty selection.
 * Construction PCR: EN 15804 is appended to a non-empty selection when missing; nothing else changes.
 * An empty selection becomes ISO 14025:2026 and EN 15804.
 * Non-construction PCR: ISO 14025:2006 and ISO 14025:2026 are kept in their order and everything else
 * is removed; when none remains, the result is ISO 14025:2026.
 * Returns a new array; the input is not mutated.
 */
export const applyPcrChangeToStandards = (selected: string[], isConstruction: boolean): string[] => {
  const current = parseStandardKeys(selected);

  if (isConstruction) {
    if (current.length === 0) {
      return [ISO_14025_2026_ID, EN_158042012_ID];
    }
    return current.includes(EN_158042012_ID) ? current : [...current, EN_158042012_ID];
  }

  const isoStandards = current.filter(isIso14025Standard);
  return isoStandards.length > 0 ? isoStandards : [ISO_14025_2026_ID];
};
